import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import Seo from "@/components/Seo";
import AdminLayout from "@/pages/admin/AdminLayout";
import { AVAILABILITY_LABEL } from "@/lib/whatsapp";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Search, MoreVertical, Pencil, CheckCircle2, Lock, Clock, EyeOff, Trash2, Star, Loader2,
} from "lucide-react";

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 font-heading text-3xl font-black">{value}</div>
    </div>
  );
}

const STATUS_STYLE = {
  available: "bg-green-100 text-green-800",
  coming_soon: "bg-amber-100 text-amber-800",
  rented: "bg-zinc-200 text-zinc-700",
  hidden: "bg-red-100 text-red-700",
};

export default function Dashboard() {
  const { refresh } = useSite();
  const [summary, setSummary] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, v] = await Promise.all([
      api.get("/admin/summary"),
      api.get("/admin/vehicles", { params: search ? { search } : {} }),
    ]);
    setSummary(s.data);
    setVehicles(v.data);
    setLoading(false);
    refresh();
  }, [search, refresh]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const setStatus = async (id, availability) => {
    await api.patch(`/admin/vehicles/${id}/status`, { availability });
    toast.success(`Marked ${AVAILABILITY_LABEL[availability]}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/vehicles/${id}`);
    toast.success("Vehicle deleted");
    load();
  };

  return (
    <AdminLayout>
      <Seo title="Admin Dashboard" noindex path="/admin" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-heading text-3xl font-black tracking-tight">Fleet Management</h1>
        <Link to="/admin/vehicles/new" data-testid="add-vehicle-btn" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#00E676] px-6 font-bold text-black transition-transform hover:-translate-y-0.5">
          <Plus className="h-5 w-5" /> Add Vehicle
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard label="Total Vehicles" value={summary.total ?? 0} testid="stat-total" />
        <StatCard label="Available Now" value={summary.available ?? 0} testid="stat-available" />
        <StatCard label="Currently Rented" value={summary.rented ?? 0} testid="stat-rented" />
        <StatCard label="Coming Soon" value={summary.coming_soon ?? 0} testid="stat-coming" />
        <StatCard label="Hidden" value={summary.hidden ?? 0} testid="stat-hidden" />
      </div>

      <div className="mt-8 flex items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reg, make or model" data-testid="admin-search" className="h-11 pl-9" />
        </div>
      </div>

      <div className="mt-4 overflow-x-auto border border-border bg-white">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>
        ) : vehicles.length === 0 ? (
          <div className="py-16 text-center text-zinc-500">No vehicles. Click "Add Vehicle" to get started.</div>
        ) : (
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-border bg-surface text-left text-[11px] font-bold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="p-3">Photo</th><th className="p-3">Vehicle</th><th className="p-3">Reg</th>
                <th className="p-3">PHV Status</th><th className="p-3">Availability</th>
                <th className="p-3">No Ins.</th><th className="p-3">With Ins.</th>
                <th className="p-3">Mileage</th><th className="p-3">Featured</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {vehicles.map((v) => (
                <tr key={v.id} data-testid={`admin-row-${v.slug}`} className="hover:bg-surface/50">
                  <td className="p-3">
                    {v.main_image ? (
                      <img src={resolveImg(v.main_image)} alt={v.model} className="h-12 w-16 rounded object-cover" />
                    ) : <div className="h-12 w-16 rounded bg-surface" />}
                  </td>
                  <td className="p-3 font-semibold">
                    {v.year} {v.make} {v.model}
                    {v.is_demo && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">DEMO</span>}
                    {!v.published && <span className="ml-2 rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600">DRAFT</span>}
                  </td>
                  <td className="p-3 text-zinc-500">{v.registration || "—"}</td>
                  <td className="p-3 text-xs">{v.phv_licence_status}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLE[v.availability]}`}>{AVAILABILITY_LABEL[v.availability]}</span>
                  </td>
                  <td className="p-3 font-bold">{v.weekly_price_without_insurance ? `£${Math.round(v.weekly_price_without_insurance)}` : "—"}</td>
                  <td className="p-3">{v.weekly_price_with_insurance ? `£${Math.round(v.weekly_price_with_insurance)}` : "—"}</td>
                  <td className="p-3 text-zinc-500">{v.mileage?.toLocaleString()}</td>
                  <td className="p-3">{v.featured ? <Star className="h-4 w-4 fill-[#00E676] text-[#00E676]" /> : "—"}</td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`admin-actions-${v.slug}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-surface"><MoreVertical className="h-4 w-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/vehicles/${v.id}`} data-testid={`edit-${v.slug}`}><Pencil className="mr-2 h-4 w-4" /> Edit</Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setStatus(v.id, "available")} data-testid={`mark-available-${v.slug}`}><CheckCircle2 className="mr-2 h-4 w-4 text-green-600" /> Mark Available</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(v.id, "rented")} data-testid={`mark-rented-${v.slug}`}><Lock className="mr-2 h-4 w-4" /> Mark Rented</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(v.id, "coming_soon")}><Clock className="mr-2 h-4 w-4 text-amber-600" /> Coming Soon</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(v.id, "hidden")} data-testid={`hide-${v.slug}`}><EyeOff className="mr-2 h-4 w-4" /> Hide</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="flex w-full items-center px-2 py-1.5 text-sm text-red-600 hover:bg-red-50" data-testid={`delete-${v.slug}`}><Trash2 className="mr-2 h-4 w-4" /> Delete</button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this vehicle?</AlertDialogTitle>
                              <AlertDialogDescription>Are you sure you want to permanently delete this vehicle? This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(v.id)} data-testid={`confirm-delete-${v.slug}`} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
